import {
  adminInventoryPageSchema,
  adminDeviceDetailSchema,
  adminDeviceEnrollmentSchema,
  adminDevicesPageSchema,
  adminMutationResultSchema,
  adminOverviewSchema,
  adminProductDetailSchema,
  adminSaleDetailSchema,
  adminSalesFilterOptionsSchema,
  adminSalesFiltersSchema,
  adminSalesPageSchema,
  adminResidentDetailSchema,
  adminResidentFiltersSchema,
  adminResidentsPageSchema,
  adminStaffDetailSchema,
  adminStaffFiltersSchema,
  adminStaffPageSchema,
  adminSessionContextSchema,
  type AdminCategoryInput,
  type AdminInventoryFilters,
  type AdminInventoryPage,
  type AdminDeviceDetail,
  type AdminDeviceEnrollment,
  type AdminDeviceFilters,
  type AdminDevicesPage,
  type AdminMutationResult,
  type AdminOverview,
  type AdminResidentDetail,
  type AdminResidentFilters,
  type AdminResidentInput,
  type AdminResidentsPage,
  type AdminProductDetail,
  type AdminProductInput,
  type AdminSaleDetail,
  type AdminSalesFilterOptions,
  type AdminSalesFilters,
  type AdminSalesPage,
  type AdminSessionContext,
  type AdminStaffCreateInput,
  type AdminStaffDetail,
  type AdminStaffFilters,
  type AdminStaffInput,
  type AdminStaffPage,
  type AdminStaffPinInput,
  type AdminStockOperationInput,
} from '@kiosk/contracts';
import {
  createClient,
  type AuthChangeEvent,
  type RealtimeChannel,
  type Session,
  type SupabaseClient,
} from '@supabase/supabase-js';

import { parseAdminEnvironment } from './environment';

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

type AdminDatabase = {
  public: {
    Tables: {
      sync_change_log: {
        Row: {
          sequence: number;
          entity_type: string;
          entity_id: string;
          operation: string;
          payload: Json;
          created_at: string;
        };
        Insert: never;
        Update: never;
        Relationships: [];
      };
    };
    Views: Record<string, never>;
    Functions: {
      admin_session_context: { Args: Record<PropertyKey, never>; Returns: Json };
      admin_overview: { Args: Record<PropertyKey, never>; Returns: Json };
      admin_sales_page: { Args: { p_filters?: Json }; Returns: Json };
      admin_sales_filter_options: { Args: Record<PropertyKey, never>; Returns: Json };
      admin_sale_detail: { Args: { p_sale_id: string }; Returns: Json };
      admin_inventory_page: { Args: { p_filters?: Json }; Returns: Json };
      admin_product_detail: {
        Args: { p_product_id: string; p_cursor?: Json; p_limit?: number };
        Returns: Json;
      };
      admin_save_product: { Args: { p_payload: Json }; Returns: Json };
      admin_set_product_active: {
        Args: { p_product_id: string; p_active: boolean; p_mutation_id: string };
        Returns: Json;
      };
      admin_save_category: { Args: { p_payload: Json }; Returns: Json };
      admin_apply_stock_operation: { Args: { p_payload: Json }; Returns: Json };
      admin_mutation_status: { Args: { p_mutation_id: string }; Returns: Json };
      admin_residents_page: { Args: { p_filters?: Json }; Returns: Json };
      admin_resident_detail: {
        Args: { p_resident_id: string; p_cursor?: Json; p_limit?: number };
        Returns: Json;
      };
      admin_save_resident: { Args: { p_payload: Json }; Returns: Json };
      admin_staff_page: { Args: { p_filters?: Json }; Returns: Json };
      admin_staff_detail: { Args: { p_staff_id: string }; Returns: Json };
      admin_save_staff: { Args: { p_payload: Json }; Returns: Json };
      admin_set_staff_pin: { Args: { p_payload: Json }; Returns: Json };
      admin_devices_page: { Args: { p_filters?: Json }; Returns: Json };
      admin_device_detail: { Args: { p_device_id: string }; Returns: Json };
      admin_issue_device_enrollment: { Args: { p_payload: Json }; Returns: Json };
      admin_revoke_device: {
        Args: { p_device_id: string; p_mutation_id: string; p_reason: string };
        Returns: Json;
      };
    };
    Enums: Record<string, never>;
    CompositeTypes: Record<string, never>;
  };
};

export type AdminService = {
  getSession: () => Promise<Session | null>;
  getSessionContext: () => Promise<AdminSessionContext>;
  loadOverview: () => Promise<AdminOverview>;
  loadSales: (filters: AdminSalesFilters) => Promise<AdminSalesPage>;
  loadSalesFilterOptions: () => Promise<AdminSalesFilterOptions>;
  loadSaleDetail: (id: string) => Promise<AdminSaleDetail>;
  loadInventory: (filters: AdminInventoryFilters) => Promise<AdminInventoryPage>;
  loadProductDetail: (
    id: string,
    cursor?: { createdAt: string; id: string },
  ) => Promise<AdminProductDetail>;
  saveProduct: (input: AdminProductInput) => Promise<AdminMutationResult>;
  setProductActive: (
    id: string,
    active: boolean,
    mutationId: string,
  ) => Promise<AdminMutationResult>;
  saveCategory: (input: AdminCategoryInput) => Promise<AdminMutationResult>;
  applyStockOperation: (input: AdminStockOperationInput) => Promise<AdminMutationResult>;
  getMutationStatus: (mutationId: string) => Promise<AdminMutationResult>;
  loadResidents: (filters: AdminResidentFilters) => Promise<AdminResidentsPage>;
  loadResidentDetail: (
    id: string,
    cursor?: { createdAt: string; id: string },
  ) => Promise<AdminResidentDetail>;
  saveResident: (input: AdminResidentInput) => Promise<AdminMutationResult>;
  loadStaff: (filters: AdminStaffFilters) => Promise<AdminStaffPage>;
  loadStaffDetail: (id: string) => Promise<AdminStaffDetail>;
  saveStaff: (input: AdminStaffInput) => Promise<AdminMutationResult>;
  createStaff: (input: AdminStaffCreateInput) => Promise<AdminMutationResult>;
  setStaffPin: (input: AdminStaffPinInput) => Promise<AdminMutationResult>;
  loadDevices: (filters: AdminDeviceFilters) => Promise<AdminDevicesPage>;
  loadDeviceDetail: (id: string) => Promise<AdminDeviceDetail>;
  issueDeviceEnrollment: (input: {
    mutationId: string;
    deviceId?: string;
    friendlyName: string;
  }) => Promise<AdminDeviceEnrollment>;
  revokeDevice: (id: string, mutationId: string, reason: string) => Promise<AdminMutationResult>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  onAuthStateChange: (
    handler: (event: AuthChangeEvent, session: Session | null) => void,
  ) => () => void;
  onOperationalChange: (handler: () => void) => () => void;
};

export type AdminAccessResult =
  | { status: 'signed-out' }
  | { status: 'denied'; message: string }
  | { status: 'authorized'; identity: AdminSessionContext };

export async function authorizeAdminSession(service: AdminService): Promise<AdminAccessResult> {
  const session = await service.getSession();
  if (session === null) return { status: 'signed-out' };
  try {
    return { status: 'authorized', identity: await service.getSessionContext() };
  } catch {
    await service.signOut();
    return {
      status: 'denied',
      message: 'This account does not have active Manager or Admin access.',
    };
  }
}

function messageFrom(error: { message: string } | null): string {
  return error?.message ?? 'The server did not return the requested data.';
}

export function createAdminService(values: Record<string, unknown>): AdminService {
  const environment = parseAdminEnvironment(values);
  const client = createClient<AdminDatabase>(
    environment.VITE_SUPABASE_URL,
    environment.VITE_SUPABASE_ANON_KEY,
    {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    },
  );
  return makeAdminService(client);
}

export function makeAdminService(client: SupabaseClient<AdminDatabase>): AdminService {
  let realtimeChannel: RealtimeChannel | null = null;

  return {
    async getSession() {
      const { data, error } = await client.auth.getSession();
      if (error !== null) throw new Error(messageFrom(error));
      return data.session;
    },

    async getSessionContext() {
      const { data, error } = await client.rpc('admin_session_context');
      if (error !== null) throw new Error(messageFrom(error));
      return adminSessionContextSchema.parse(data);
    },

    async loadOverview() {
      const { data, error } = await client.rpc('admin_overview');
      if (error !== null) throw new Error(messageFrom(error));
      return adminOverviewSchema.parse(data);
    },

    async loadSales(filters) {
      const payload = adminSalesFiltersSchema.parse(filters) as Json;
      const { data, error } = await client.rpc('admin_sales_page', { p_filters: payload });
      if (error !== null) throw new Error(messageFrom(error));
      return adminSalesPageSchema.parse(data);
    },

    async loadSalesFilterOptions() {
      const { data, error } = await client.rpc('admin_sales_filter_options');
      if (error !== null) throw new Error(messageFrom(error));
      return adminSalesFilterOptionsSchema.parse(data);
    },

    async loadSaleDetail(id) {
      const { data, error } = await client.rpc('admin_sale_detail', { p_sale_id: id });
      if (error !== null) throw new Error(messageFrom(error));
      return adminSaleDetailSchema.parse(data);
    },

    async loadInventory(filters) {
      const { data, error } = await client.rpc('admin_inventory_page', {
        p_filters: filters as Json,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminInventoryPageSchema.parse(data);
    },

    async loadProductDetail(id, cursor) {
      const { data, error } = await client.rpc('admin_product_detail', {
        p_product_id: id,
        ...(cursor === undefined ? {} : { p_cursor: cursor as Json }),
        p_limit: 30,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminProductDetailSchema.parse(data);
    },

    async saveProduct(input) {
      const { data, error } = await client.rpc('admin_save_product', { p_payload: input as Json });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async setProductActive(id, active, mutationId) {
      const { data, error } = await client.rpc('admin_set_product_active', {
        p_product_id: id,
        p_active: active,
        p_mutation_id: mutationId,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async saveCategory(input) {
      const { data, error } = await client.rpc('admin_save_category', { p_payload: input as Json });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async applyStockOperation(input) {
      const { data, error } = await client.rpc('admin_apply_stock_operation', {
        p_payload: input as Json,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async getMutationStatus(mutationId) {
      const { data, error } = await client.rpc('admin_mutation_status', {
        p_mutation_id: mutationId,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async loadResidents(filters) {
      const payload = adminResidentFiltersSchema.parse(filters) as Json;
      const { data, error } = await client.rpc('admin_residents_page', { p_filters: payload });
      if (error !== null) throw new Error(messageFrom(error));
      return adminResidentsPageSchema.parse(data);
    },

    async loadResidentDetail(id, cursor) {
      const { data, error } = await client.rpc('admin_resident_detail', {
        p_resident_id: id,
        ...(cursor === undefined ? {} : { p_cursor: cursor as Json }),
        p_limit: 30,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminResidentDetailSchema.parse(data);
    },

    async saveResident(input) {
      const { data, error } = await client.rpc('admin_save_resident', { p_payload: input as Json });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async loadStaff(filters) {
      const payload = adminStaffFiltersSchema.parse(filters) as Json;
      const { data, error } = await client.rpc('admin_staff_page', { p_filters: payload });
      if (error !== null) throw new Error(messageFrom(error));
      return adminStaffPageSchema.parse(data);
    },

    async loadStaffDetail(id) {
      const { data, error } = await client.rpc('admin_staff_detail', { p_staff_id: id });
      if (error !== null) throw new Error(messageFrom(error));
      return adminStaffDetailSchema.parse(data);
    },

    async saveStaff(input) {
      const { data, error } = await client.rpc('admin_save_staff', { p_payload: input as Json });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async createStaff(input) {
      const { data, error } = await client.rpc('admin_save_staff', { p_payload: input as Json });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async setStaffPin(input) {
      const credential = await deriveKioskPinCredential(input.pin);
      const { data, error } = await client.rpc('admin_set_staff_pin', {
        p_payload: { ...input, credential } as Json,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async loadDevices(filters) {
      const { data, error } = await client.rpc('admin_devices_page', {
        p_filters: filters as Json,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminDevicesPageSchema.parse(data);
    },

    async loadDeviceDetail(id) {
      const { data, error } = await client.rpc('admin_device_detail', { p_device_id: id });
      if (error !== null) throw new Error(messageFrom(error));
      return adminDeviceDetailSchema.parse(data);
    },

    async issueDeviceEnrollment(input) {
      const { data, error } = await client.rpc('admin_issue_device_enrollment', {
        p_payload: input as Json,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminDeviceEnrollmentSchema.parse(data);
    },

    async revokeDevice(id, mutationId, reason) {
      const { data, error } = await client.rpc('admin_revoke_device', {
        p_device_id: id,
        p_mutation_id: mutationId,
        p_reason: reason,
      });
      if (error !== null) throw new Error(messageFrom(error));
      return adminMutationResultSchema.parse(data);
    },

    async signIn(email, password) {
      const { error } = await client.auth.signInWithPassword({ email, password });
      if (error !== null) throw new Error(messageFrom(error));
    },

    async signOut() {
      const { error } = await client.auth.signOut();
      if (error !== null) throw new Error(messageFrom(error));
    },

    onAuthStateChange(handler) {
      const { data } = client.auth.onAuthStateChange(handler);
      return () => data.subscription.unsubscribe();
    },

    onOperationalChange(handler) {
      realtimeChannel = client
        .channel('admin-operational-invalidations')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sync_change_log' }, handler)
        .subscribe();
      return () => {
        if (realtimeChannel !== null) void client.removeChannel(realtimeChannel);
        realtimeChannel = null;
      };
    },
  };
}

function hexadecimal(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function deriveKioskPinCredential(pin: string): Promise<{
  algorithm: 'PBKDF2-HMAC-SHA256';
  saltHex: string;
  verifierHex: string;
  iterations: number;
  keyLength: number;
}> {
  if (!/^\d{4}$/.test(pin)) throw new Error('PIN must contain exactly four digits.');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const iterations = 120_000;
  const keyLength = 32;
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    key,
    keyLength * 8,
  );
  return {
    algorithm: 'PBKDF2-HMAC-SHA256',
    saltHex: hexadecimal(salt),
    verifierHex: hexadecimal(new Uint8Array(bits)),
    iterations,
    keyLength,
  };
}
